import { Injectable, inject } from '@angular/core';
import { LngLat, Map, Marker } from 'maplibre-gl';
import { fromEvent, Subject, Subscription } from 'rxjs';
import { first } from 'rxjs/operators';
import device from 'current-device';
import { MapService } from './map.service';
import { MapDrawerHelperService } from './map-drawer-helper.service';
import { WaypointService } from '../core/waypoint.service';
import { UtilsService } from '../utils/utils.service';
import { SettingsService } from '../account/settings.service';
import { AccountService } from '../account/account.service';
import { LanguageService } from '../account/language.service';
import { MobileResolutionService } from '../platform/mobile-resolution.service';
import { ChargingStationDetails, ChargerProvider } from 'src/app/models/charging-station';
import { Route, RoutePlanElement, RoutePlanType } from 'src/app/models/route';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DisplayMode } from 'src/app/models/display-mode';
import { SvgRecolorService } from '../media/svg-recolor.service';
import { VehicleService } from '../vehicle/vehicle.service';

export const enum PopupAlign {
  Left,
  Right,
  Top
}

@Injectable({
  providedIn: 'root',
})
export class MapChargingPopupService {

  private mapInstance: Map | null = null;
  private lastOpenedChargingStationPopup: ChargingStationDetails | null = null;
  private chargingStationPopupMarker: Marker | null = null;
  private chargerPopupSubscriptions: Subscription[] = [];
  private lastRouteData: Route | null = null;

  public statusChanged$ = new Subject<void>();

  private readonly mapService = inject(MapService);
  private readonly mapDrawerHelperService = inject(MapDrawerHelperService);
  private readonly waypointService = inject(WaypointService);
  private readonly utilsService = inject(UtilsService);
  private readonly settingsService = inject(SettingsService);
  private readonly accountService = inject(AccountService);
  private readonly languageService = inject(LanguageService);
  private readonly mobileResolutionService = inject(MobileResolutionService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly svgRecolorService = inject(SvgRecolorService);
  private readonly vehicleService = inject(VehicleService);

  public init(map: Map): void {
    this.mapInstance = map;
  }

  public destroy(): void {
    this.chargerPopupSubscriptions.forEach(s => s.unsubscribe());
    this.chargerPopupSubscriptions = [];
    this.removeChargingStationPopup();
    this.mapInstance = null;
  }

  public getLastOpenedPopup(): ChargingStationDetails | null {
    return this.lastOpenedChargingStationPopup;
  }

  public setLastRouteData(routeData: Route | null): void {
    this.lastRouteData = routeData;
  }

  // ─── Charging Station Popup ──────────────────────────────────────────

  public loadChargingStationPopup(align: PopupAlign, widthAnchor: number, heightAnchor: number,
    chargingStationId: string, statusSelector: boolean, planBtn: boolean, searched: boolean): void {
    this.chargerPopupSubscriptions.forEach(s => s.unsubscribe());
    this.chargerPopupSubscriptions = [];

    this.mapService.getChargingStationDetail([chargingStationId]).subscribe((resp) => {
      if (resp && resp.length > 0) {
        this.drawChargingStationPopup(align, widthAnchor, heightAnchor, resp[0], statusSelector, planBtn, searched);
      }
    });
  }

  private drawChargingStationPopup(align: PopupAlign, widthAnchor: number, heightAnchor: number,
    chargingStation: ChargingStationDetails, statusSelector: boolean, planBtn: boolean, searched: boolean): void {
    this.lastOpenedChargingStationPopup = chargingStation;
    this.removeChargingStationPopup();

    const map = this.mapInstance!;

    let tooltip = "";

    switch (align) {
      case PopupAlign.Left:
        tooltip += "<div class='map-tooltip arrow-to-right cs-tooltip ev-card'>";
        break;
      case PopupAlign.Right:
        tooltip += "<div class='map-tooltip arrow-to-left cs-tooltip ev-card'>";
        break;
      case PopupAlign.Top:
        if (planBtn || searched) {
          tooltip += "<div class='map-tooltip arrow-to-bottom top cs-tooltip ev-card'>";
        } else {
          tooltip += "<div class='map-tooltip arrow-to-bottom top rounded-body cs-tooltip ev-card'>";
        }
        break;
    }

    // Title - clean address text
    tooltip += `<div class="cs-title">${this.escapeHtml(chargingStation.title)}</div>`;

    tooltip += `<div class="body">`;

    // Operator & info card
    let operatorName = '';
    let operatorUrl = '';
    if (chargingStation.opid != undefined && chargingStation.opid != null && chargingStation.opid != -1) {
      const operator = this.mapService.getChargingOperatorByID(chargingStation.opid);
      if (operator) {
        operatorName = operator.name;
        operatorUrl = operator.url || '';
      }
    }

    const hasInfo = operatorName || chargingStation.phone || chargingStation.cost || chargingStation.provider == ChargerProvider.OCM;

    if (hasInfo) {
      tooltip += `<div class="cs-info-card">`;

      if (operatorName) {
        tooltip += `<div class="cs-operator-name">${this.escapeHtml(operatorName)}</div>`;
      }

      if (operatorUrl) {
        tooltip += `<div class="cs-info-row">
          <span class="cs-info-icon" data-recolor-src="assets/icons/dark/map/info_icon_web.svg"></span>
          <a target="_blank" href="${this.escapeHtml(operatorUrl)}">${this.escapeHtml(operatorUrl)}</a></div>`;
      }

      if (chargingStation.phone) {
        tooltip += `<div class="cs-info-row">
          <img class="cs-info-icon" src="assets/icons/${this.settingsService.getMap().Skin}/map/info_icon_phone.svg" />
          <a target="_blank" href="tel:${this.escapeHtml(chargingStation.phone)}">${this.escapeHtml(chargingStation.phone)}</a></div>`;
      }

      if (chargingStation.cost) {
        if (this.utilsService.validURL(chargingStation.cost)) {
          tooltip += `<div class="cs-info-row">
            <img class="cs-info-icon" src="assets/icons/${this.settingsService.getMap().Skin}/map/info_icon_price.svg" />
            <a target="_blank" href="${this.escapeHtml(chargingStation.cost)}">${this.escapeHtml(chargingStation.cost)}</a></div>`;
        } else {
          tooltip += `<div class="cs-info-row">
            <img class="cs-info-icon" src="assets/icons/${this.settingsService.getMap().Skin}/map/info_icon_price.svg" />
            <span>${this.escapeHtml(chargingStation.cost)}</span></div>`;
        }
      }

      if (chargingStation.provider == ChargerProvider.OCM) {
        const ocmId = this.utilsService.getOCMId(chargingStation.id);
        tooltip += `<div class="cs-info-row">
          <img class="cs-info-icon" src="assets/icons/default/map/info_icon_ocm.svg" />
          <a target="_blank" href="https://openchargemap.org/site/poi/details/${this.escapeHtml(ocmId)}">${this.languageService.languageJSON.Map_Popup_MoreInfo}</a></div>`;
      }

      tooltip += `</div>`;
    }

    // Plug card
    if (chargingStation.plugs.length > 0) {
      tooltip += `<div class="cs-plug-card">`;
      for (let j = 0; j < chargingStation.plugs.length; j++) {
        const chargePower = Math.round(chargingStation.plugs[j].power * 10) / 10;
        const chargerType = chargingStation.plugs[j].type;
        const carPower = chargerType == 3 || chargerType == 5 || chargerType == 6
          ? this.vehicleService.getSelectedCar().ChargePower
          : this.vehicleService.getSelectedCar().FastChargePower;

        const chargePowerText = (carPower > 0 && carPower < chargePower)
          ? chargePower + "kW (" + carPower + " kW)"
          : chargePower + " kW";

        const isFast = chargePower >= 50;
        const powerClass = isFast ? 'fast' : 'standard';

        tooltip += `<div class="cs-plug-row ${powerClass}">
          <div class="charger-icon"><span data-recolor-src="/assets/icons/dark/plug/${this.utilsService.chargingstationsImg[chargingStation.plugs[j].type - 1][0]}"></span>`;

        if (this.accountService.getUserSubscriptionType().includes('proplus.') && (chargingStation.plugs[j].statusCount ?? 0) > 0) {
          tooltip += `<div class="cs-availability ${chargingStation.plugs[j].available! > 0 ? 'available' : 'not-available'}">${chargingStation.plugs[j].available}/${chargingStation.plugs[j].statusCount}</div>`;
        }

        tooltip += `</div>
          <span class="cs-plug-text">${chargingStation.plugs[j].count} x ${this.utilsService.chargingstationsImg[chargingStation.plugs[j].type - 1][1]} - ${chargePowerText}</span></div>`;
      }
      tooltip += `</div>`;
    }

    // Status selector
    if (statusSelector) {
      tooltip += `<div class="cs-status-wrapper">`;
      tooltip += `<div class="cs-status-card status status-opener">`;
      if (this.settingsService.getContainsDisabledChargingStationId(chargingStation.id)) {
        tooltip += this.getChargerDisabledStatusHtmlElementsSelected();
      } else if (this.settingsService.getContainsFavoriteChargingStationId(chargingStation.id)) {
        tooltip += this.getChargerFavoriteStatusHtmlElementsSelected();
      } else {
        tooltip += this.getChargerNormalStatusHtmlElementsSelected();
      }
      tooltip += `<img class="status-opener-icon" src="assets/icons/${this.settingsService.getMap().Skin}/ui/open_arrow.svg" /></div>`;

      tooltip += `<div class="status-dropdown">
        <div class="status" id="normal-btn">${this.getChargerNormalStatusHtmlElements()}</div>
        <div class="divider"></div>
        <div class="status" id="favorite-btn">${this.getChargerFavoriteStatusHtmlElements()}</div>
        <div class="divider"></div>
        <div class="status" id="disabled-btn">${this.getChargerDisabledStatusHtmlElements()}</div>
      </div>`;
      tooltip += `</div>`;
    }
    tooltip += `</div>`;

    if (planBtn) {
      if (this.waypointService.getWaypointsParams().length >= 5) {
        tooltip += "<div class='ev-button primary plan-btn inactive'>" + this.languageService.languageJSON.Map_Popup_ChargeHere + "</div>";
      } else {
        tooltip += "<div class='ev-button primary plan-btn'>" + this.languageService.languageJSON.Map_Popup_ChargeHere + "</div>";
      }
    }
    if (searched) {
      tooltip += "<div class='plan-btn plan-btn-container'></div>";
    }
    tooltip += "</div>";

    // Create a 0x0 anchor element so MapLibre's translate(-50%, -50%) has no effect
    const popupEl = document.createElement('div');

    // Position the tooltip via an absolutely-positioned wrapper
    const tooltipWrapper = document.createElement('div');
    tooltipWrapper.innerHTML = tooltip;
    tooltipWrapper.style.position = 'absolute';

    switch (align) {
      case PopupAlign.Left:
        tooltipWrapper.style.right = `${Math.abs(widthAnchor)}px`;
        tooltipWrapper.style.transform = 'translateY(-50%)';
        break;
      case PopupAlign.Right:
        tooltipWrapper.style.left = `${Math.abs(widthAnchor)}px`;
        tooltipWrapper.style.transform = 'translateY(-50%)';
        break;
      case PopupAlign.Top:
        tooltipWrapper.style.bottom = `${heightAnchor}px`;
        tooltipWrapper.style.transform = 'translateX(-50%)';
        break;
    }

    popupEl.appendChild(tooltipWrapper);

    this.chargingStationPopupMarker = new Marker({ element: popupEl })
      .setLngLat([chargingStation.lon, chargingStation.lat])
      .addTo(map);

    this.applySvgRecolors(tooltipWrapper);
    this.centerMapOnPopup(map, tooltipWrapper);

    // Bind event listeners after DOM is ready
    setTimeout(() => {
      this.bindPopupEventListeners(chargingStation, statusSelector, planBtn);
    });
  }

  // Pans the map so the freshly-drawn popup box sits in the middle of the
  // viewport and is fully visible, regardless of which direction it opens or
  // how tall/wide it is. Runs on the next frame so the popup has been laid out.
  private centerMapOnPopup(map: Map, tooltipWrapper: HTMLElement): void {
    requestAnimationFrame(() => {
      const tooltipEl = tooltipWrapper.querySelector<HTMLElement>('.map-tooltip');
      if (!tooltipEl) return;

      const rect = tooltipEl.getBoundingClientRect();
      const canvasRect = map.getCanvas().getBoundingClientRect();

      // Popup centre relative to the map canvas.
      let targetX = rect.left + rect.width / 2 - canvasRect.left;
      const targetY = rect.top + rect.height / 2 - canvasRect.top;

      // In landscape route mode a side panel covers the left of the map, so
      // nudge the popup towards the right half to keep it clear of the panel.
      if (!this.mobileResolutionService.isPortrait() && this.waypointService.getSelectedDisplayMode() == DisplayMode.ROUTE) {
        targetX = targetX - 125;
      }

      // Move the geo point currently under the popup centre to the viewport
      // centre; because the popup is anchored to the station, it travels with
      // the pan and ends up centred.
      const newCenter = map.unproject([targetX, targetY]);
      map.panTo(newCenter, { duration: 500 });
    });
  }

  private applySvgRecolors(root: HTMLElement): void {
    const color = getComputedStyle(document.body)
      .getPropertyValue('--base-gradient-color-2').trim();

    root.querySelectorAll<HTMLElement>('[data-recolor-src]').forEach(placeholder => {
      const src = placeholder.getAttribute('data-recolor-src')!;
      const cls = placeholder.getAttribute('class') ?? '';
      this.chargerPopupSubscriptions.push(
        this.svgRecolorService.recolor(src, { color }).subscribe(svgMarkup => {
          placeholder.outerHTML = svgMarkup.replace('<svg', `<svg class="${cls}"`);
        })
      );
    });
  }

  private bindPopupEventListeners(chargingStation: ChargingStationDetails, statusSelector: boolean, planBtn: boolean): void {
    if (planBtn) {
      if (this.waypointService.getWaypointsParams().length >= 5) {
        this.snackBar.open(this.languageService.languageJSON.InfoSidebar_WaypointLimitReached, "",
          { duration: 5000, panelClass: 'snackbar-info' });
      }

      const planBtnEl = document.getElementsByClassName('plan-btn')[0];
      if (planBtnEl) {
        this.chargerPopupSubscriptions.push(
          fromEvent(planBtnEl, 'click').pipe(first()).subscribe((event: any) => {
            if (event.isTrusted) {
              this.removeChargingStationPopup();
              this.waypointService.addChargingStationWaypoint(
                chargingStation.title,
                chargingStation.lat,
                chargingStation.lon,
                chargingStation.id,
                this.getChargeHereClosestRouteIndex(chargingStation)
              );
            }
          })
        );
      }
    }

    if (statusSelector) {
      const openerEl = document.getElementsByClassName('status-opener')[0];
      const dropdownEl = document.getElementsByClassName('status-dropdown')[0];
      const tooltipEl = document.getElementsByClassName('cs-tooltip')[0];
      const normalBtn = document.getElementById('normal-btn');
      const favoriteBtn = document.getElementById('favorite-btn');
      const disabledBtn = document.getElementById('disabled-btn');

      if (openerEl) {
        this.chargerPopupSubscriptions.push(fromEvent(openerEl, 'click').subscribe((e: any) => {
          if (e.isTrusted && dropdownEl) { dropdownEl.classList.add("show"); }
        }));
      }
      if (dropdownEl) {
        this.chargerPopupSubscriptions.push(fromEvent(dropdownEl, 'click').subscribe((e: any) => {
          if (e.isTrusted) { dropdownEl.classList.remove("show"); }
        }));
      }
      if (tooltipEl) {
        this.chargerPopupSubscriptions.push(fromEvent(tooltipEl, 'click').subscribe((e: any) => {
          if (e.isTrusted) {
            let removeClass = true;
            const path = e.composedPath();
            if (path) {
              for (let i = 0; i < path.length; i++) {
                if (path[i].className && path[i].className?.includes("status status-opener") ||
                  path[i].className && path[i].className?.includes("status-dropdown")) {
                  removeClass = false;
                  break;
                }
              }
            }
            if (removeClass && dropdownEl) { dropdownEl.classList.remove("show"); }
          }
        }));
      }
      if (normalBtn) {
        this.chargerPopupSubscriptions.push(fromEvent(normalBtn, 'click').subscribe((e: any) => {
          if (e.isTrusted) {
            this.settingsService.setToNormalChargingStation(chargingStation.id);
            this.changeStatusOpener(this.getChargerNormalStatusHtmlElementsSelected());
          }
        }));
      }
      if (favoriteBtn) {
        this.chargerPopupSubscriptions.push(fromEvent(favoriteBtn, 'click').subscribe((e: any) => {
          if (e.isTrusted) {
            this.settingsService.setToFavoriteChargingStation(chargingStation.id);
            this.changeStatusOpener(this.getChargerFavoriteStatusHtmlElementsSelected());
          }
        }));
      }
      if (disabledBtn) {
        this.chargerPopupSubscriptions.push(fromEvent(disabledBtn, 'click').subscribe((e: any) => {
          if (e.isTrusted) {
            this.settingsService.setToDisabledChargingStation(chargingStation.id);
            this.changeStatusOpener(this.getChargerDisabledStatusHtmlElementsSelected());
          }
        }));
      }
    }
  }

  public removeChargingStationPopup(): void {
    if (this.chargingStationPopupMarker) {
      this.chargingStationPopupMarker.remove();
      this.chargingStationPopupMarker = null;
    }
    this.chargerPopupSubscriptions.forEach(s => s.unsubscribe());
    this.chargerPopupSubscriptions = [];
  }

  // Re-draws the currently open charging-station popup (if any) so its imperatively
  // built HTML picks up newly loaded translation texts. No-op when no popup is visible.
  public redrawOpenedChargingStationPopup(): void {
    if (this.chargingStationPopupMarker && this.lastOpenedChargingStationPopup) {
      this.redrawChargingStationPopup(this.lastOpenedChargingStationPopup);
    }
  }

  public redrawChargingStationPopup(chargingStationPopup: ChargingStationDetails): void {
    let chargerPlannedRouteOccurence: number = 0;
    if (this.lastRouteData) {
      const routeChargers: RoutePlanElement[] = this.lastRouteData.RoutePlan.filter((routePlanEl) => {
        return routePlanEl.Type == RoutePlanType.Charger;
      });
      chargerPlannedRouteOccurence = routeChargers.filter((routeCharger) =>
        routeCharger.OperatorId == chargingStationPopup.id
      ).length;
    }
    this.removeChargingStationPopup();
    const favoriteCharger = this.settingsService.getContainsFavoriteChargingStationId(chargingStationPopup.id);

    if (chargerPlannedRouteOccurence == 0) {
      const heightAnchor = favoriteCharger ? 24 : 20;
        this.drawChargingStationPopup(PopupAlign.Top, 0, heightAnchor, chargingStationPopup, true, true, false);
    } else if (chargerPlannedRouteOccurence == 1) {
      this.drawChargingStationPopup(PopupAlign.Top, 0, 80, chargingStationPopup, true, false, false);
    } else {
      this.drawChargingStationPopup(PopupAlign.Top, 0, 61 + (19 * chargerPlannedRouteOccurence), chargingStationPopup, true, false, false);
    }
  }

  // ─── Status Helpers ──────────────────────────────────────────────────

  private changeStatusOpener(htmlEl: string): void {
    const statusOpener = document.querySelector(".status.status-opener");
    if (statusOpener) {
      const icon = statusOpener.querySelector(".status-icon");
      const state = statusOpener.querySelector(".status-state");
      if (icon) icon.remove();
      if (state) state.remove();
      const statusEL = document.createElement('div');
      statusEL.innerHTML = htmlEl;
      for (let i = statusEL.children.length - 1; i >= 0; i--) {
        statusOpener.prepend(statusEL.children[i]);
      }
    }
    if (this.accountService.getIsAuthorized()) {
      this.accountService.setDisabledChargingStations().subscribe();
      this.accountService.setFavoriteChargingStations().subscribe();
    }
    this.statusChanged$.next();
  }

  private getChargerNormalStatusHtmlElements(): string {
    return this.mapDrawerHelperService.getChargerHtmlElements(this.settingsService.getMap().Skin, "normal",
      this.languageService.languageJSON.Global_Normal, '');
  }

  private getChargerNormalStatusHtmlElementsSelected(): string {
    return this.mapDrawerHelperService.getChargerHtmlElements(this.settingsService.getMap().Skin, "normal",
      this.languageService.languageJSON.Global_Normal, this.languageService.languageJSON.Map_Popup_Status);
  }

  private getChargerDisabledStatusHtmlElements(): string {
    return this.mapDrawerHelperService.getChargerHtmlElements(this.settingsService.getMap().Skin, "disabled",
      this.languageService.languageJSON.Map_Popup_Disabled, '');
  }

  private getChargerDisabledStatusHtmlElementsSelected(): string {
    return this.mapDrawerHelperService.getChargerHtmlElements(this.settingsService.getMap().Skin, "disabled",
      this.languageService.languageJSON.Map_Popup_Disabled, this.languageService.languageJSON.Map_Popup_Status);
  }

  private getChargerFavoriteStatusHtmlElements(): string {
    return this.mapDrawerHelperService.getChargerHtmlElements(this.settingsService.getMap().Skin, "favorite",
      this.languageService.languageJSON.Map_Popup_Favorite, '');
  }

  private getChargerFavoriteStatusHtmlElementsSelected(): string {
    return this.mapDrawerHelperService.getChargerHtmlElements(this.settingsService.getMap().Skin, "favorite",
      this.languageService.languageJSON.Map_Popup_Favorite, this.languageService.languageJSON.Map_Popup_Status);
  }

  // ─── Utility Methods ─────────────────────────────────────────────────

  private getChargeHereClosestRouteIndex(chargingStation: ChargingStationDetails): number {
    let closestRoutePointIdx = -1;
    let closestRoutePointDistance = -1;

    if (this.waypointService.getSelectedDisplayMode() == DisplayMode.RANGE) {
      return this.waypointService.getWaypointsParams().length;
    }

    if (this.lastRouteData) {
      for (let i = 0; i < this.lastRouteData.RoutePoints.length; i++) {
        const actDist = this.utilsService.distanceBetweenCoordinates(
          [chargingStation.lat, chargingStation.lon],
          [this.lastRouteData.RoutePoints[i].Location.lat, this.lastRouteData.RoutePoints[i].Location.lng]);
        if (actDist < 30 * 1000 && (closestRoutePointDistance == -1 || actDist < closestRoutePointDistance)) {
          closestRoutePointIdx = i;
          closestRoutePointDistance = actDist;
        }
      }

      if (closestRoutePointIdx != -1) {
        let i = 0;
        let reachedWaypoints = 0;
        while (this.lastRouteData.Turns[i] && this.lastRouteData.Turns[i].RoutePointIdxStart <= closestRoutePointIdx) {
          if (this.lastRouteData.Turns[i].TypeIndex == 103 || this.lastRouteData.Turns[i].TypeIndex == 98 ||
            this.lastRouteData.Turns[i].TypeIndex == 106) {
            reachedWaypoints++;
          }
          i++;
        }
        return reachedWaypoints + 1;
      } else {
        return this.waypointService.getWaypointsParams().length;
      }
    } else {
      return this.waypointService.getWaypointsParams().length;
    }
  }

  private escapeHtml(str: string): string {
    if (!str) return '';
    const div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    return div.innerHTML;
  }
}
